import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, X, Shield, Clock, RotateCcw } from 'lucide-react';

import { BACKEND_URL } from '../config/api';

const STATUS_META = {
  pending:  { label: 'Pending',  cls: 'bg-amber-50 text-amber-700 border-amber-200',   icon: Clock },
  approved: { label: 'Approved', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: Check },
  declined: { label: 'Declined', cls: 'bg-slate-100 text-slate-600 border-slate-200',   icon: X },
  revoked:  { label: 'Revoked',  cls: 'bg-slate-100 text-slate-500 border-slate-200',   icon: RotateCcw },
};

export default function PhoneAccessRequestsInbox() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/tradespeople/me/phone-access-requests`, {
        withCredentials: true,
      });
      setRows(r.data?.requests || []);
    } catch (e) {
      // Non-fatal — the inbox simply stays empty.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const act = async (request_id, action) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/tradespeople/me/phone-access-requests/${request_id}/action`,
        { action },
        { withCredentials: true }
      );
      toast.success(`Request ${action}d`);
      refresh();
    } catch (e) {
      toast.error(`Could not ${action} request`);
    }
  };

  if (loading) return null;
  if (!rows.length) return null;

  return (
    <div className="card mb-6" data-testid="phone-access-requests-inbox">
      <h3 className="text-lg font-bold text-slate-900 mb-2 flex items-center gap-2">
        <Shield className="h-5 w-5 text-orange-500" />
        Phone Access Requests
      </h3>
      <p className="text-sm text-slate-500 mb-4">
        Approved employers can request to see your phone number. You choose who to approve.
      </p>
      <ul className="space-y-3">
        {rows.map((r) => {
          const m = STATUS_META[r.status] || STATUS_META.pending;
          const Icon = m.icon;
          return (
            <li
              key={r.request_id}
              className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-slate-200 p-4"
              data-testid={`phone-access-request-${r.request_id}`}
            >
              <div className="flex-1">
                <p className="font-semibold text-slate-900">
                  {r.employer_name || 'Approved employer'}
                </p>
                <p className="text-xs text-slate-500">
                  Requested {new Date(r.created_at).toLocaleString('en-GB')}
                </p>
              </div>
              <span
                className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border ${m.cls}`}
                data-testid={`phone-access-request-${r.request_id}-status`}
              >
                <Icon className="h-3.5 w-3.5" />
                {m.label}
              </span>
              <div className="flex gap-2">
                {r.status === 'pending' && (
                  <>
                    <button
                      type="button"
                      onClick={() => act(r.request_id, 'approve')}
                      className="btn-primary text-sm px-3 py-1.5"
                      data-testid={`phone-access-request-${r.request_id}-approve`}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      onClick={() => act(r.request_id, 'decline')}
                      className="btn-secondary text-sm px-3 py-1.5"
                      data-testid={`phone-access-request-${r.request_id}-decline`}
                    >
                      Decline
                    </button>
                  </>
                )}
                {r.status === 'approved' && (
                  <button
                    type="button"
                    onClick={() => act(r.request_id, 'revoke')}
                    className="btn-secondary text-sm px-3 py-1.5"
                    data-testid={`phone-access-request-${r.request_id}-revoke`}
                  >
                    Revoke
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
